import {
  matchRank,
  type ContainerResult,
  type MatchCategory,
  type MoleculeResult,
  type SearchResults
} from '@peruselab/api-client/moleculeSearch';

export const ALL_CATEGORIES: readonly MatchCategory[] = [
  'IDENTICAL',
  'SAME_SKELETON',
  'SUBSTRUCTURE',
  'SUPERSTRUCTURE',
  'SIMILAR'
];

export type EntityType = 'molecules' | 'drawings' | 'reactions' | 'illustrations';
export const ALL_TYPES: readonly EntityType[] = ['molecules', 'drawings', 'reactions', 'illustrations'];

export type SortKey = 'scoreDesc' | 'scoreAsc' | 'title';

/** A drawing / reaction / illustration of the user's that contains a card's molecule. */
export interface ContainerRef {
  readonly id: string;
  readonly title: string | null;
}

export type ContainerKind = Exclude<EntityType, 'molecules'>;
export const CONTAINER_KINDS: readonly ContainerKind[] = ['drawings', 'reactions', 'illustrations'];

/**
 * #688 -- one result card per molecule. The search answers with separate lists (the molecule
 * itself, and each drawing / reaction / illustration that contains it), and every one of those
 * depicts the same molecule -- so they are merged here into a single card that says where else
 * the molecule lives (`containers`).
 */
export interface ResultCard {
  readonly key: string;
  // Display name: the molecule's registered name (#519), else the user's own title for a
  // container holding it (drawings first), else its molecular formula once `formulas` has it
  // (ADR 0007 P6d), else its id.
  readonly label: string;
  // The best match among everything found for this molecule: Identical, then Same skeleton
  // (#1161), then higher score.
  readonly category: MatchCategory;
  readonly score: number;
  readonly thumbnailMoleculeId: string;
  /** Found as a molecule in its own right (not only inside a drawing etc.). */
  readonly foundAsMolecule: boolean;
  readonly containers: Readonly<Record<ContainerKind, readonly ContainerRef[]>>;
  // #658 -- in a Markush match, which of the drawing's structures matched (`R1 = F · n = 2`).
  readonly matchedBy?: string;
  /** #940 -- where the query sits in this molecule (a substructure hit), for the picture. */
  readonly highlight?: { readonly atomIds: readonly string[]; readonly bondIds: readonly string[] };
  /** #1060 -- only the user's own tenant holds this molecule (not public): the card says so. */
  readonly private?: boolean;
  /** #1064 -- the user's space drew it, but nothing saved uses it yet. */
  readonly provisional?: boolean;
}

/**
 * #743 -- a reaction found by a search gets its own card (reactions are content in their own
 * right, under Show -> Reactions), besides the reaction icon on the molecule card it contains.
 */
export interface ReactionCard {
  readonly kind: 'reaction';
  readonly key: string;
  readonly reactionId: string;
  /** The reaction's title ('' when untitled, so it sorts first by title). */
  readonly label: string;
  /** Its best match: the matched molecule's category and score. */
  readonly category: MatchCategory;
  readonly score: number;
  /** What it was found by: the matched molecule's name or formula, when known. */
  readonly matched: string | null;
}

/**
 * #794 -- a drawing that is more than its molecule (it has text, shapes, arrows or other
 * annotations) gets a card of its own when Show is narrowed to Drawings alone. It takes its
 * molecule's match and sits right after that molecule's card, most recently edited first.
 */
export interface DrawingCard {
  readonly kind: 'drawing';
  readonly key: string;
  readonly drawingId: string;
  /** The drawing's title ('' when untitled). */
  readonly label: string;
  readonly category: MatchCategory;
  readonly score: number;
  /** The molecule card it follows. */
  readonly moleculeKey: string;
  readonly updatedAt: string | null;
}

/** A card in the results: a molecule's (with where else it lives), a reaction's or a drawing's. */
export interface DocumentCard {
  readonly kind: 'document';
  readonly key: string;
  readonly documentId: string;
  readonly entityType: ContainerKind;
  readonly label: string;
  readonly category: MatchCategory;
  readonly score: number;
}
export type ResultEntry = (ResultCard & { readonly kind?: 'molecule' }) | ReactionCard | DrawingCard | DocumentCard;

/** One entry of the search answer, before merging by molecule. Filters apply at this level. */
export interface ResultSource {
  readonly kind: EntityType;
  readonly id: string;
  readonly moleculeId: string;
  /** The molecule's registered name, or the container's own title. */
  readonly name: string | null;
  readonly category: MatchCategory;
  readonly score: number;
  /** #746 -- a reaction found by its own words: a card of its own, not a place where the molecule
   *  (`moleculeId` is only one of its participants) lives. */
  readonly standalone?: boolean;
  /** #794 -- drawings: more than the molecule, and when last edited. */
  readonly hasAnnotations?: boolean;
  readonly updatedAt?: string | null;
  /** #940 -- molecules: the atoms and bonds the query matched (substructure hits). */
  readonly highlight?: { readonly atomIds: readonly string[]; readonly bondIds: readonly string[] };
  /** #1060 -- molecules: private to the user's own tenant. */
  readonly private?: boolean;
  /** #1064 -- molecules: drawn by the user's space, not yet in anything saved. */
  readonly provisional?: boolean;
}

/** moleculeId -> molecular formula, populated lazily by `useMoleculeFormulas`. */
export type MoleculeFormulas = ReadonlyMap<string, string>;
const NO_FORMULAS: MoleculeFormulas = new Map();

export type ResultsWithMatchedBy = SearchResults & { readonly matchedByText?: Readonly<Record<string, string>> };

function moleculeSource(match: MoleculeResult): ResultSource {
  return {
    kind: 'molecules',
    id: match.moleculeId,
    moleculeId: match.moleculeId,
    name: match.name ?? null,
    category: match.category,
    score: match.tanimotoScore,
    ...(match.visibility === 'private' ? { private: true } : {}),
    ...(match.provisional ? { provisional: true } : {}),
    ...(match.matchedAtomIds && match.matchedAtomIds.length > 0
      ? { highlight: { atomIds: match.matchedAtomIds, bondIds: match.matchedBondIds ?? [] } }
      : {})
  };
}

function containerSource(result: ContainerResult, kind: ContainerKind): ResultSource {
  return {
    kind,
    id: result.documentId,
    moleculeId: result.sourceMoleculeId ?? '',
    ...(result.textMatch ? { standalone: true } : {}),
    name: result.title ?? null,
    category: result.category,
    score: result.tanimotoScore,
    ...(result.hasAnnotations ? { hasAnnotations: true } : {}),
    ...(result.updatedAt ? { updatedAt: result.updatedAt } : {})
  };
}

/** Every entry of the search answer, in the answer's order (molecules, then containers, then
 *  #746's reactions found by their own words). */
export function resultSources(
  results: SearchResults & { readonly reactionsByText?: readonly ContainerResult[] }
): ResultSource[] {
  return [
    ...results.molecules.map(moleculeSource),
    ...results.drawings.map((r) => containerSource(r, 'drawings')),
    ...results.reactions.map((r) => containerSource(r, 'reactions')),
    ...results.illustrations.map((r) => containerSource(r, 'illustrations')),
    ...(results.reactionsByText ?? []).map((r) => ({ ...containerSource(r, 'reactions'), standalone: true }))
  ];
}

const betterMatch = (a: ResultSource, b: ResultSource) =>
  matchRank(a.category) - matchRank(b.category) || b.score - a.score;

/** #688 -- merges sources into one card per molecule (in first-seen order; sort afterwards). */
export function mergeByMolecule(
  sources: readonly ResultSource[],
  formulas: MoleculeFormulas = NO_FORMULAS,
  matchedByText?: Readonly<Record<string, string>>
): ResultCard[] {
  const groups = new Map<string, ResultSource[]>();
  for (const source of sources) {
    if (source.standalone) continue; // #746: a reaction card only
    const group = groups.get(source.moleculeId);
    if (group) group.push(source);
    else groups.set(source.moleculeId, [source]);
  }
  return [...groups.entries()].map(([moleculeId, group]) => {
    const best = [...group].sort(betterMatch)[0]!;
    const molecule = group.find((source) => source.kind === 'molecules');
    const containers = Object.fromEntries(
      CONTAINER_KINDS.map((kind) => {
        const seen = new Set<string>();
        const refs = group
          .filter((source) => source.kind === kind && !seen.has(source.id) && seen.add(source.id))
          .map((source) => ({ id: source.id, title: source.name }));
        return [kind, refs];
      })
    ) as Record<ContainerKind, ContainerRef[]>;
    const containerTitle = CONTAINER_KINDS.flatMap((kind) => containers[kind]).find((ref) => ref.title)?.title;
    const matchedBy = matchedByText?.[moleculeId];
    const highlight = molecule?.highlight;
    return {
      key: moleculeId,
      label: molecule?.name ?? containerTitle ?? formulas.get(moleculeId) ?? moleculeId,
      category: best.category,
      score: best.score,
      thumbnailMoleculeId: moleculeId,
      foundAsMolecule: molecule !== undefined,
      containers,
      ...(matchedBy ? { matchedBy } : {}),
      ...(highlight ? { highlight } : {}),
      ...(molecule?.private ? { private: true } : {}),
      ...(molecule?.provisional ? { provisional: true } : {})
    };
  });
}

/** #743 -- one card per reaction among `sources`, from its best match (first-seen order). */
export function reactionCards(
  sources: readonly ResultSource[],
  formulas: MoleculeFormulas = NO_FORMULAS
): ReactionCard[] {
  const moleculeNames = new Map(
    sources
      .filter((source) => source.kind === 'molecules' && source.name)
      .map((source) => [source.moleculeId, source.name!])
  );
  const best = new Map<string, ResultSource>();
  for (const source of sources) {
    if (source.kind !== 'reactions') continue;
    const current = best.get(source.id);
    if (!current || betterMatch(source, current) < 0) best.set(source.id, source);
  }
  return [...best.values()].map((source) => ({
    kind: 'reaction',
    key: `reaction:${source.id}`,
    reactionId: source.id,
    label: source.name ?? '',
    category: source.category,
    score: source.score,
    // #746: found by its own words, it matched no molecule -- nothing to say "Contains" about.
    matched: source.standalone
      ? null
      : (moleculeNames.get(source.moleculeId) ?? formulas.get(source.moleculeId) ?? null)
  }));
}

export function documentCards(sources: readonly ResultSource[]): DocumentCard[] {
  return [
    ...new Map(
      sources
        .filter((source) => source.kind === 'illustrations' && source.standalone)
        .map((source) => [source.id, source])
    ).values()
  ].map((source) => ({
    kind: 'document',
    key: `document:${source.id}`,
    documentId: source.id,
    entityType: 'illustrations',
    label: source.name ?? '',
    category: source.category,
    score: source.score
  }));
}

/** #794 -- one card per drawing that is more than its molecule, from its best match. */
export function drawingCards(sources: readonly ResultSource[]): DrawingCard[] {
  const best = new Map<string, ResultSource>();
  for (const source of sources) {
    if (source.kind !== 'drawings' || !source.hasAnnotations) continue;
    const current = best.get(source.id);
    if (!current || betterMatch(source, current) < 0) best.set(source.id, source);
  }
  return [...best.values()].map((source) => ({
    kind: 'drawing',
    key: `drawing:${source.id}`,
    drawingId: source.id,
    label: source.name ?? '',
    category: source.category,
    score: source.score,
    moleculeKey: source.moleculeId,
    updatedAt: source.updatedAt ?? null
  }));
}

/** #794 -- each drawing card right after its molecule's card, most recently edited first; one
 *  whose molecule card isn't shown goes at the end. */
export function placeDrawingCards(cards: readonly ResultEntry[], drawings: readonly DrawingCard[]): ResultEntry[] {
  const byMolecule = new Map<string, DrawingCard[]>();
  for (const drawing of drawings)
    byMolecule.set(drawing.moleculeKey, [...(byMolecule.get(drawing.moleculeKey) ?? []), drawing]);
  for (const group of byMolecule.values()) group.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  const placed: ResultEntry[] = [];
  for (const card of cards) {
    placed.push(card);
    if (card.kind === undefined || card.kind === 'molecule') {
      placed.push(...(byMolecule.get(card.key) ?? []));
      byMolecule.delete(card.key);
    }
  }
  for (const rest of byMolecule.values()) placed.push(...rest);
  return placed;
}

/**
 * #932 -- how many copies of a structure (saved drawings and illustrations) are folded into the
 * molecule cards shown -- each reachable from its card's drawing or illustration icon (#688).
 */
export function foldedCount(cards: readonly ResultEntry[]): number {
  return cards.reduce(
    (sum, card) =>
      card.kind === undefined || card.kind === 'molecule'
        ? sum + card.containers.drawings.length + card.containers.illustrations.length
        : sum,
    0
  );
}

/** The search answer as merged cards (no filtering). */
export function resultCards(results: ResultsWithMatchedBy, formulas: MoleculeFormulas = NO_FORMULAS): ResultCard[] {
  return mergeByMolecule(resultSources(results), formulas, results.matchedByText);
}

type Sortable = Pick<ResultCard, 'label' | 'category' | 'score'>;

export function sortCards<T extends Sortable>(cards: readonly T[], key: SortKey): T[] {
  const copy = [...cards];
  if (key === 'title') {
    copy.sort((a, b) => a.label.localeCompare(b.label));
    return copy;
  }
  // #711 -- best first puts an identical match first, then by score: a similar molecule can share
  // the identical one's score (the same fingerprint), and must not outrank it. #1161: a same-
  // skeleton match comes next, whatever its score.
  const identicalFirst = (a: Sortable, b: Sortable) => matchRank(a.category) - matchRank(b.category);
  copy.sort((a, b) => (key === 'scoreDesc' ? identicalFirst(a, b) || b.score - a.score : a.score - b.score));
  return copy;
}

export function toggleInSet<T>(set: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}
